import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useAppSelector,
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    Divider,
} from '../../../../base';
import { reportsSelectors } from '../../../../actions';
import {
    TimeToHireChartType,
    TIME_TO_HIRE_CHART_COLORS,
    TIME_TO_HIRE_METRIC_KEYS,
    getReportPeriodLabel,
    exportComparisonToExcel,
} from '../../../../common';
import ChartTypeToggle from '../ChartTypeToggle';
import TimeToHireEmptyState from '../TimeToHireEmptyState';
import ComparisonBarChart from './ComparisonBarChart';
import ComparisonLineChart from './ComparisonLineChart';
import ComparisonPieCharts from './ComparisonPieCharts';
import {
    StyledCard,
    StyledHeader,
    StyledLegendRow,
    StyledLegendItem,
    StyledColorDot,
} from './JobComparisonView.styles';

const JobComparisonView = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [chartType, setChartType] = useState<TimeToHireChartType>(
        TimeToHireChartType.BAR
    );

    const timeToHireData = useAppSelector(reportsSelectors.getTimeToHireData);

    if (!timeToHireData || timeToHireData.view !== 'comparison') return null;

    const dateRangeLabel = getReportPeriodLabel(
        {
            startDate: timeToHireData.startDate,
            endDate: timeToHireData.endDate,
            timeFrame: timeToHireData.timeFrame,
        },
        t
    );

    const totalDays = timeToHireData.jobs.reduce(
        (jobSum, job) =>
            jobSum +
            TIME_TO_HIRE_METRIC_KEYS.reduce(
                (metricSum, key) =>
                    metricSum + Math.round(job[key]?.averageDays ?? 0),
                0
            ),
        0
    );

    const isEmpty = totalDays === 0;

    return (
        <StyledCard>
            <StyledHeader container>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('reportsPage.timeToHireReport.jobComparison.title')}
                    </Typography>
                    <Typography variant="body2" color={theme.palette.grey[500]}>
                        {dateRangeLabel}
                    </Typography>
                </Box>
                {!isEmpty && (
                    <ChartTypeToggle
                        activeType={chartType}
                        onChange={setChartType}
                    />
                )}
            </StyledHeader>

            <Divider sx={{ borderColor: theme.palette.grey[300] }} />
            {isEmpty ? (
                <TimeToHireEmptyState />
            ) : (
                <Box p={2}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <StyledLegendRow container>
                            {timeToHireData.jobs.map((job, index) => (
                                <StyledLegendItem key={job.jobId}>
                                    <StyledColorDot
                                        dotColor={
                                            TIME_TO_HIRE_CHART_COLORS[
                                                index %
                                                    TIME_TO_HIRE_CHART_COLORS.length
                                            ]
                                        }
                                    />
                                    <Typography variant="body2">
                                        {job.jobTitle}
                                    </Typography>
                                </StyledLegendItem>
                            ))}
                        </StyledLegendRow>
                        <IconButton
                            size="small"
                            onClick={() =>
                                exportComparisonToExcel(timeToHireData, t)
                            }
                        >
                            <Icon
                                name="FileDownloadOutlinedIcon"
                                size={16}
                            />
                        </IconButton>
                    </Box>

                    {chartType === TimeToHireChartType.BAR && (
                        <ComparisonBarChart data={timeToHireData} />
                    )}
                    {chartType === TimeToHireChartType.LINE && (
                        <ComparisonLineChart data={timeToHireData} />
                    )}
                    {chartType === TimeToHireChartType.PIE && (
                        <ComparisonPieCharts data={timeToHireData} />
                    )}
                </Box>
            )}
        </StyledCard>
    );
};

export default JobComparisonView;
